import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

let cachedOrgId = null;
let cachedOrg = null;
let inflight = null;

/**
 * Organization profile from Org Admin → Org Settings (logo, address, contact).
 * Cached per organizationId for reuse across print views.
 */
export function useOrganizationProfile() {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? null;
  const [organization, setOrganization] = useState(() =>
    organizationId && cachedOrgId === organizationId ? cachedOrg : null
  );

  useEffect(() => {
    if (!organizationId) {
      setOrganization(null);
      return;
    }
    if (cachedOrgId === organizationId && cachedOrg) {
      setOrganization(cachedOrg);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        if (!inflight || cachedOrgId !== organizationId) {
          inflight = axios.get(`/api/organizations/${organizationId}`).then((res) => res.data);
          cachedOrgId = organizationId;
        }
        const data = await inflight;
        cachedOrg = data;
        if (!cancelled) setOrganization(data);
      } catch (error) {
        console.error('Error loading organization for print:', error);
        if (!cancelled) setOrganization(null);
      } finally {
        inflight = null;
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return organization;
}

export function formatOrgAddressLines(org) {
  if (!org) return [];
  const lines = [];
  if (org.address) lines.push(org.address);
  const cityLine = [org.city, org.district].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);
  const regionLine = [org.state, org.postalCode].filter(Boolean).join(' ');
  if (regionLine) lines.push(regionLine);
  if (org.country) lines.push(org.country);
  return lines;
}
